import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

import { buildTaggedUrl } from './taggedUrlPreview/buildTaggedUrl';
import { URLS_FIELD, readBuilderForm } from './taggedUrlPreview/readBuilderForm';
import PreviewErrorBoundary from './taggedUrlPreview/PreviewErrorBoundary.jsx';
import PreviewContent from './taggedUrlPreview/PreviewContent.jsx';

// Live preview of the tagged URL(s) on the Form Mode builder, new and edit (#2627).
// One island, two views of one snapshot: a section in the page below the form,
// and a right-edge drawer that exists only while that section is out of view.
const DISPLAY_LIMIT = 3;
const PANEL_ID = 'tagged-url-preview-panel';
const OPEN_KEY = 'taggedUrlPreview/open';
// The extension popup keeps its own choice, apart from the full builder's.
const OVERLAY_OPEN_KEY = `${OPEN_KEY}/overlay`;

// Storage can throw (private mode, blocked site data); a failed read means closed.
function readOpen(key) {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeOpen(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Nothing to do: the choice just won't survive a reload.
  }
}

const builderForm = () => document.querySelector(URLS_FIELD)?.form || null;

// select2 and presets fire `change` with jQuery's .trigger(), which only
// reaches handlers bound through jQuery, not native addEventListener
// listeners. Listen through window.jQuery, the instance application.js exposes.
function subscribe(form, handler) {
  const $form = window.jQuery(form);
  $form.on('input.taggedUrlPreview change.taggedUrlPreview', handler);
  return () => $form.off('.taggedUrlPreview');
}

function Preview({
  params, tagFormat, orderCustomByValue, overlay = false,
}) {
  const openKey = overlay ? OVERLAY_OPEN_KEY : OPEN_KEY;
  const [form] = useState(builderForm);
  const [snapshot, setSnapshot] = useState(() => (form ? readBuilderForm(form, params) : null));
  const [open, setOpen] = useState(() => readOpen(openKey));
  const [showAll, setShowAll] = useState(false);
  // Without IntersectionObserver the in-page section is assumed visible: no drawer.
  const [sectionVisible, setSectionVisible] = useState(true);

  const sectionRef = useRef(null);

  const handleRef = useRef(null);
  const closeRef = useRef(null);
  const mounted = useRef(false);
  const drawerRef = useRef(null);
  const panelRef = useRef(null);

  // Saved on the user's clicks only, never from scrolling.
  const choose = (value) => {
    setOpen(value);
    writeOpen(openKey, value);
  };

  const drawerOpen = open && !sectionVisible;

  // The drawer only exists while the section is out of view. The user's
  // open/closed choice is kept while the section is visible, so the drawer
  // returns as they left it.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      const { isIntersecting } = entries[entries.length - 1];
      setSectionVisible(isIntersecting);
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, [form]);

  useEffect(() => {
    if (!form) return undefined;
    setSnapshot(readBuilderForm(form, params));
    let frame = null;
    const handler = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setSnapshot(readBuilderForm(form, params));
      });
    };
    // Other React islands set hidden inputs without firing input/change; React
    // mirrors a controlled input's value to its `value` attribute.
    const observer = new MutationObserver(handler);
    observer.observe(form, { subtree: true, attributes: true, attributeFilter: ['value'] });
    const unsubscribe = subscribe(form, handler);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      unsubscribe();
    };
  }, [form, params]);

  // The drawer is fixed over the page's bottom right, so while open reserve its
  // height (plus its bottom offset, which the QA badge changes) as page space;
  // otherwise it can cover a control that has nowhere to scroll clear of it.
  useEffect(() => {
    const drawer = drawerRef.current;
    const panel = panelRef.current;
    if (!drawerOpen || !drawer || !panel || typeof ResizeObserver === 'undefined') return undefined;

    const { style } = document.body;
    const previous = style.paddingBottom;
    const reserve = () => {
      const offset = parseFloat(window.getComputedStyle(drawer).bottom) || 0;
      style.paddingBottom = `${panel.offsetHeight + offset}px`;
    };
    reserve();
    const observer = new ResizeObserver(reserve);
    observer.observe(panel);
    return () => {
      observer.disconnect();
      style.paddingBottom = previous;
    };
  }, [drawerOpen]);

  // The focused button hides on every toggle; hand focus to the other one.
  // Scrolling the section in and out never changes `open`, so it moves nothing.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    (open ? closeRef : handleRef).current?.focus();
  }, [open]);

  if (!form) return null;

  const options = { tagFormat, orderCustomByValue };
  const count = snapshot.urls.length;
  const urls = count ? snapshot.urls : [''];
  // Only the shown lines are built: a bulk paste can hold hundreds of URLs.
  const lines = (showAll ? urls : urls.slice(0, DISPLAY_LIMIT))
    .map((url) => buildTaggedUrl(url, params, snapshot.values, options));
  // No URL yet: stand in a placeholder for the empty base.
  if (!count) lines[0][0] = { type: 'url-placeholder', label: 'Destination URL' };

  const content = {
    count,
    lines,
    total: urls.length,
    limit: DISPLAY_LIMIT,
    showAll,
    onToggleAll: () => setShowAll(!showAll),
  };

  return (
    <>
      <div ref={sectionRef} className="tagged-url-preview-section">
        <PreviewContent {...content} />
      </div>
      {!sectionVisible && (
        <div ref={drawerRef} className={`tagged-url-preview${drawerOpen ? ' tagged-url-preview--open' : ''}`}>
          <button
            type="button"
            ref={handleRef}
            className="tagged-url-preview__handle"
            aria-expanded={drawerOpen}
            aria-controls={PANEL_ID}
            aria-label="Open preview"
            onClick={() => choose(true)}
          >
            <i className="icon-play4 tagged-url-preview__handle-caret" aria-hidden="true" />
            <span className="tagged-url-preview__handle-label" aria-hidden="true">PREVIEW</span>
          </button>
          <div id={PANEL_ID} ref={panelRef} className="tagged-url-preview__panel" role="region" aria-label="Tagged URL preview">
            {drawerOpen && (
              <PreviewContent {...content}>
                <button
                  type="button"
                  ref={closeRef}
                  className="close tagged-url-preview__close"
                  aria-label="Close preview"
                  onClick={() => choose(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </PreviewContent>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const paramShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['utm', 'custom']).isRequired,
  label: PropTypes.string.isRequired,
  inputName: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  generated: PropTypes.bool.isRequired,
});

Preview.propTypes = {
  params: PropTypes.arrayOf(paramShape).isRequired,
  tagFormat: PropTypes.shape({
    forceLowercase: PropTypes.bool,
    spaceReplace: PropTypes.string,
    preservedCharacters: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  orderCustomByValue: PropTypes.bool.isRequired,
  overlay: PropTypes.bool,
};

export default function TaggedUrlPreview(props) {
  return (
    <PreviewErrorBoundary>
      <Preview {...props} />
    </PreviewErrorBoundary>
  );
}
